import {
  type Allocation,
  type AllocationValueTypeEnum,
  type CustomerOrder,
  type Order,
  type Security,
  type User,
  abbreviateId,
  ACTION,
  AutoHedgingStatus,
  Button,
  ButtonVariants,
  Divider,
  Flex,
  formattedDate,
  HStack,
  Icon,
  IconButton,
  IconName,
  IndicatorBadge,
  isCalendarSpread,
  isMultileg,
  isOrder,
  isTalosUser,
  Menu,
  MenuItem,
  MixpanelEvent,
  MixpanelEventProperty,
  OrderStatus,
  SmartSummary,
  Text,
  Tooltip,
  useDisclosure,
  useDynamicCallback,
  useMixpanel,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { modifyOrder } from '../../../../components/OMS/NewOrder/OrderSlice';
import { openView } from '../../../../components/OMS/OMSSlice';
import { OMSView } from '../../../../components/OMS/OMSView';
import { useAdminUtils, useFeatureFlag, useOrderAccess, useRoleAuth } from '../../../../hooks';
import { prettifySubAccount, useOrders, useSubAccounts } from '../../../../providers';
import { useAppStateDispatch } from '../../../../providers/AppStateProvider';
import { useTradingSettings } from '../../../../providers/TradingSettingsContext';
import { prettifyAllocations } from '../../../../utils/allocation';
import { useAssertOrderModifiable } from '../../../../utils/order';
import { CancelSelectedDialog, ForceCancelSelectedDialog } from '../../../Blotters/CancelSelectedDialog';
import { ResumeDialog } from '../../../Blotters/Orders/Dialogs/ResumeDialog';
import { OrderSummaryItem } from './OrderSummaryItem';
import { OrderInfo, TopWrapper } from './styles';
import { Warnings } from './Warnings';

export function OrderDetailsHeader({
  order,
  security,
  width,
  customerOrder,
  subAccountAllocations,
  allocationValueType,
  user,
  onClose,
}: {
  order: Order;
  security: Security | undefined;
  width: number;
  customerOrder: CustomerOrder | undefined;
  subAccountAllocations: Allocation[] | undefined;
  allocationValueType: AllocationValueTypeEnum | undefined;
  user: User;
  onClose: () => void;
}) {
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const { archive, pause, resume, cancel, unarchive } = useOrders();
  const dispatch = useAppStateDispatch();
  const { subAccountsByName } = useSubAccounts();
  const { enableDynamicDeltaHedging, enableArchiveOrders } = useFeatureFlag();
  const { confirmOrderCancels, confirmOrderResume } = useTradingSettings();

  const subAccountString = subAccountAllocations?.length
    ? prettifyAllocations(subAccountAllocations, allocationValueType, order.Currency, subAccountsByName)
    : prettifySubAccount(order.SubAccount, subAccountsByName) || '-';

  const onModifyOrder = useDynamicCallback((order: Order) => {
    mixpanel.track(MixpanelEvent.ModifyOrder);
    dispatch(modifyOrder(order));
    dispatch(openView(OMSView.NewOrderForm));
  });

  const onPauseOrder: typeof pause = useDynamicCallback(orderID => {
    mixpanel.track(MixpanelEvent.PauseOrder, { [MixpanelEventProperty.ID]: orderID });
    pause(orderID);
  });
  const resumeDialog = useDisclosure();
  const onResumeOrder = useDynamicCallback(() => {
    if (confirmOrderResume) {
      resumeDialog.open();
    } else {
      mixpanel.track(MixpanelEvent.ResumeOrder, { [MixpanelEventProperty.ID]: order.OrderID });
      resume(order.OrderID);
    }
  });
  const onCancelOrder = useDynamicCallback(() => {
    if (confirmOrderCancels) {
      cancelDialog.open();
    } else {
      mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: order.OrderID });
      cancel(order.OrderID);
    }
  });
  const onArchiveOrder: typeof archive = useDynamicCallback(orderID => {
    mixpanel.track(MixpanelEvent.ArchiveOrder, { [MixpanelEventProperty.ID]: orderID });
    archive(orderID);
  });
  const onUnarchiveOrder: typeof archive = useDynamicCallback(orderID => {
    mixpanel.track(MixpanelEvent.UnarchiveOrder, { [MixpanelEventProperty.ID]: orderID });
    unarchive(orderID);
  });

  const { forceCancelOrder } = useAdminUtils();

  const checkOrderAccess = useOrderAccess();
  const canModifyOrder = checkOrderAccess(order);
  const assertOrderModifiable = useAssertOrderModifiable();
  const cancelDialog = useDisclosure({ onOpen: () => mixpanel.track(MixpanelEvent.CancelAdmin) });
  const forceCancelDialog = useDisclosure({ onOpen: () => mixpanel.track(MixpanelEvent.CancelForce) });

  const orderTypeLabel = useMemo(() => {
    if (isMultileg(security)) {
      return 'Multi-Leg Order';
    } else if (isCalendarSpread(security)) {
      return 'Calendar Spread';
    } else {
      return 'Order';
    }
  }, [security]);

  return (
    <>
      <TopWrapper data-testid="order-details-header">
        <OrderInfo>
          <HStack gap="spacing8" justifyContent="flex-start">
            <Icon icon={IconName.Hashtag} size="fontSizeLarge" />
            <Flex flexDirection="column" alignItems="flex-start" gap="spacing4">
              <IndicatorBadge children="ID" />
              <Text color="colorTextImportant" size="fontSizeLarge" data-testid="order-id">
                {abbreviateId(order.OrderID)}
              </Text>
            </Flex>
          </HStack>

          <Divider orientation="vertical" my="spacing4" />

          <OrderSummaryItem
            title={
              <Flex gap="spacing4" alignItems="center" data-testid="order-submit-time">
                <IndicatorBadge children={orderTypeLabel} />
                {formattedDate(order.SubmitTime)}
              </Flex>
            }
            data-testid="order-summary"
          >
            <SmartSummary
              entity={order}
              type={isOrder(order) ? 'order' : 'quote'}
              showSymbol={true}
              showStrategy={true}
              showPriceReference
            />
          </OrderSummaryItem>

          <Divider orientation="vertical" my="spacing4" />

          <OrderSummaryItem
            title={
              <Flex gap="spacing4" alignItems="center" data-testid="order-timestamp">
                <IndicatorBadge children="Filled" />
                {formattedDate(order.Timestamp)}
              </Flex>
            }
            data-testid="order-filled-summary"
          >
            <SmartSummary
              entity={order}
              type={isOrder(order) ? 'order' : 'quote'}
              showSide={false}
              qtyField="CumQty"
              priceField="AvgPx"
            />
          </OrderSummaryItem>

          {width > 1000 && (
            <>
              <Divider orientation="vertical" my="spacing4" />
              <HStack>
                <OrderSummaryItem title={<IndicatorBadge children="User" />} data-testid="order-user">
                  {order.User}
                </OrderSummaryItem>
              </HStack>

              {customerOrder != null && (
                <OrderSummaryItem title={<IndicatorBadge children="Customer" />}>
                  {customerOrder.Counterparty ?? '-'}
                </OrderSummaryItem>
              )}

              {order.Group && (
                <OrderSummaryItem title={<IndicatorBadge children="Group" />}>{order.Group ?? '-'}</OrderSummaryItem>
              )}

              <HStack>
                {(subAccountAllocations || order.SubAccount) && (
                  <Tooltip tooltip={subAccountString} placement="bottom">
                    <OrderSummaryItem
                      title={<IndicatorBadge children="Sub Account(s)" />}
                      maxWidth="100px"
                      data-testid="order-subaccount"
                    >
                      {subAccountString}
                    </OrderSummaryItem>
                  </Tooltip>
                )}
              </HStack>
            </>
          )}

          <Divider orientation="vertical" my="spacing4" />

          <OrderSummaryItem title={<IndicatorBadge children="Status" />}>
            <OrderStatus
              ordStatus={order.OrdStatus}
              decisionStatus={order.DecisionStatus}
              cumQty={order.CumQty}
              orderQty={order.OrderQty}
              pricingMode={order.PricingMode}
              isCustomerOrder={false}
            />
          </OrderSummaryItem>

          {enableDynamicDeltaHedging && order.HedgeOrderStatus && (
            <>
              <Divider orientation="vertical" my="spacing4" />
              <OrderSummaryItem title={<IndicatorBadge noWrap children="DDH Status" />}>
                <AutoHedgingStatus
                  status={order.HedgeOrderStatus.HedgeControlStatus}
                  text={order.HedgeOrderStatus.Text}
                />
              </OrderSummaryItem>
            </>
          )}
        </OrderInfo>
        <HStack gap="spacing24" p="spacing12" justifyContent="flex-end">
          <Warnings order={order} />
          <Divider orientation="vertical" my="spacing4" />
          {canModifyOrder ? (
            <>
              <HStack gap="spacing8" justifyContent="flex-end">
                {width > 1400 && (
                  <HStack gap="spacing8" justifyContent="flex-end">
                    <Button
                      disabled={!order.isCancelable || !isAuthorized(ACTION.CANCEL_ORDER)}
                      onClick={() => onCancelOrder()}
                      data-testid="ord-details-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={!assertOrderModifiable(order) || !isAuthorized(ACTION.SUBMIT_ORDER)}
                      onClick={() => onModifyOrder(order)}
                      data-testid="ord-details-modify"
                    >
                      Modify
                    </Button>
                    {order.isPausable && (
                      <IconButton
                        variant={ButtonVariants.Default}
                        icon={IconName.Pause}
                        disabled={!isAuthorized(ACTION.PAUSE_ORDER)}
                        onClick={() => onPauseOrder(order.OrderID)}
                        data-testid="ord-details-pause"
                      />
                    )}
                    {order.isResumable && (
                      <IconButton
                        variant={ButtonVariants.Primary}
                        icon={IconName.Play}
                        disabled={!isAuthorized(ACTION.RESUME_ORDER)}
                        onClick={() => onResumeOrder()}
                        data-testid="ord-details-resume"
                      />
                    )}
                    {enableArchiveOrders && order.isArchivable && (
                      <Button
                        disabled={!isAuthorized(ACTION.ARCHIVE_ORDER)}
                        onClick={() => onArchiveOrder(order.OrderID)}
                        data-testid="ord-details-archive"
                      >
                        Archive
                      </Button>
                    )}
                    {enableArchiveOrders && order.isUnArchivable && (
                      <Button
                        disabled={!isAuthorized(ACTION.ARCHIVE_ORDER)}
                        onClick={() => onUnarchiveOrder(order.OrderID)}
                        data-testid="ord-details-unarchive"
                      >
                        Unarchive
                      </Button>
                    )}
                  </HStack>
                )}
                <Menu>
                  {isTalosUser(user) && (
                    <>
                      <MenuItem
                        variant={ButtonVariants.Negative}
                        disabled={order.isComplete || !isAuthorized(ACTION.CANCEL_ORDER)}
                        onClick={() => cancelDialog.open()}
                      >
                        Cancel (admin)
                      </MenuItem>
                      <MenuItem
                        variant={ButtonVariants.Negative}
                        disabled={!isAuthorized(ACTION.FORCE_CANCEL_ORDER)}
                        onClick={() => forceCancelDialog.open()}
                      >
                        Force Cancel (admin)
                      </MenuItem>
                    </>
                  )}
                  {width < 1400 && (
                    <>
                      <MenuItem
                        variant={ButtonVariants.Negative}
                        disabled={!order.isCancelable || !isAuthorized(ACTION.CANCEL_ORDER)}
                        onClick={() => onCancelOrder()}
                      >
                        Cancel
                      </MenuItem>
                      <MenuItem
                        variant={ButtonVariants.Negative}
                        disabled={!assertOrderModifiable(order) || !isAuthorized(ACTION.SUBMIT_ORDER)}
                        onClick={() => onModifyOrder(order)}
                      >
                        Modify
                      </MenuItem>
                      {order.isPausable && (
                        <MenuItem
                          onClick={() => onPauseOrder(order.OrderID)}
                          disabled={!isAuthorized(ACTION.PAUSE_ORDER)}
                          data-testid="ord-details-pause"
                        >
                          <IconButton variant={ButtonVariants.Default} icon={IconName.Pause} />
                        </MenuItem>
                      )}
                      {order.isResumable && (
                        <MenuItem
                          onClick={() => onResumeOrder()}
                          disabled={!isAuthorized(ACTION.RESUME_ORDER)}
                          data-testid="ord-details-resume"
                        >
                          <IconButton variant={ButtonVariants.Primary} icon={IconName.Play} />
                        </MenuItem>
                      )}
                      {enableArchiveOrders && order.isArchivable && (
                        <MenuItem
                          disabled={!isAuthorized(ACTION.ARCHIVE_ORDER)}
                          onClick={() => onArchiveOrder(order.OrderID)}
                          data-testid="ord-details-archive"
                        >
                          Archive
                        </MenuItem>
                      )}
                      {enableArchiveOrders && order.isUnArchivable && (
                        <MenuItem
                          disabled={!isAuthorized(ACTION.ARCHIVE_ORDER)}
                          onClick={() => onUnarchiveOrder(order.OrderID)}
                          data-testid="ord-details-unarchive"
                        >
                          Unarchive
                        </MenuItem>
                      )}
                    </>
                  )}
                </Menu>
                <Divider orientation="vertical" my="spacing4" />
                <IconButton
                  icon={IconName.Close}
                  ghost={true}
                  onClick={onClose}
                  data-testid="order-details-close-button"
                />
              </HStack>
            </>
          ) : (
            <IconButton icon={IconName.Close} ghost={true} onClick={onClose} data-testid="order-details-close-button" />
          )}
        </HStack>
      </TopWrapper>
      <ResumeDialog selectedOrder={order} resumeDialog={resumeDialog} />
      <CancelSelectedDialog
        key="cancel"
        selectedItems={order}
        cancelSelectedDialog={cancelDialog}
        onConfirm={orders =>
          orders.forEach(order => {
            mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: order.OrderID });
            cancel(order.OrderID);
          })
        }
      />
      <ForceCancelSelectedDialog
        key="force-cancel"
        selectedItems={order}
        forceCancelSelectedDialog={forceCancelDialog}
        onConfirm={orders => orders.forEach(order => forceCancelOrder(order.OrderID))}
      />
    </>
  );
}
